<template>
  <div class="filter-item-date-range w-full md:w-576px p-4">
    <date-picker v-if="props.visible" v-model.range.number="value" v-bind="datePickerProps">
      <template #header-prev-button>
        <svg-icon name="arrow-left" class="w-4 h-4" />
      </template>
      <template #header-next-button>
        <svg-icon name="arrow-right" class="w-4 h-4" />
      </template>
    </date-picker>
  </div>
</template>

<script setup lang="ts">
import { addDays } from 'date-fns'

import { DatePicker } from 'v-calendar'
import { useAppStore } from '@/store/app'
import 'v-calendar/style.css'

import { DayModeCountMap } from '@/types/filters'
import { EdbCategoryDayMode as ApiEdbCategoryDayMode } from '@/types/api'

const { locale } = useI18n()

const app = useAppStore()

const props = withDefaults(
  defineProps<{
    modelValue: [Nullable<number>, Nullable<number>]
    dayMode: ApiEdbCategoryDayMode
    visible: boolean
  }>(),
  {
    dayMode: ApiEdbCategoryDayMode.CategoryDayModeCalendar180Days,
    visible: false,
  }
)

const emit = defineEmits<{
  (event: 'update:modelValue', value: [Nullable<number>, Nullable<number>]): void
}>()

const CURRENT_DATE = new Date().setHours(0, 0, 0, 0)

const value = computed({
  get() {
    return {
      /** NOTE:
       * 要清空 Naive UI 的 date-picker 顯示的狀態時，必須要設為空字串或 null，否則會出現資料變了但 UI 沒有變的狀況
       * 由於 null 會有型別對不上的問題，故當使用者點擊清除按鈕，外層傳入 null 時，此處將介面取得的值轉換成空字串
       */
      start: props.modelValue[0] || '',
      end: props.modelValue[1] || '',
    }
  },
  set(value) {
    if (value) emit('update:modelValue', [value.start as number, value.end as number])
  },
})

const isShowMultipleRowsCalendar = computed(() => app.isMobile)
const calendarRows = computed(() => {
  return Math.ceil(DayModeCountMap[props.dayMode] / 30) + 1
})
const datePickerProps = computed(() => ({
  // v-calendar locale map: https://github.com/nathanreyes/v-calendar/blob/master/src/utils/defaults/locales.js#L3
  locale: locale.value,
  columns: isShowMultipleRowsCalendar.value ? 1 : 2,
  rows: isShowMultipleRowsCalendar.value ? calendarRows.value : 1,
  expanded: app.isMobile || app.isTablet,
  borderless: true,
  color: 'niceday',
  minDate: selectableDateRange.value.start,
  maxDate: selectableDateRange.value.end,
  masks: {
    modelValue: 'YYYY-MM-DD',
  },
  dragAttribute: {
    highlight: {
      start: {
        fillMode: 'solid' as const,
      },
      base: {
        fillMode: 'light' as const,
      },
      end: {
        fillMode: 'solid' as const,
      },
    },
  },
}))

const selectableDateRange = computed(() => ({
  start: CURRENT_DATE,
  end: addDays(CURRENT_DATE, DayModeCountMap[props.dayMode]),
}))
</script>

<style lang="scss" src="@/assets/styles/v-calendar-overrides.scss"></style>
